import type { MindGraph, GraphNode, GraphEdge } from './types';
import type { PartInstance } from '../data/parts/partTypes';
import { makeStarterPart } from '../data/parts/roll';
import { MECH_CORE_DEF_ID, MECH_CORE_INSTANCE_ID } from '../data/mechCore';

/**
 * Graph construction + mutation helpers. IDs come from monotonic counters
 * (deterministic, no Math.random), so saves/replays reproduce identical ids.
 * Nodes reference equipped PartInstances by instanceId.
 */

export function createEmptyGraph(): MindGraph {
  return { nodes: [], edges: [], nextId: 1 };
}

export function addNode(graph: MindGraph, defId: string, instanceId: string | null, x: number, y: number, level = 1): GraphNode {
  const node: GraphNode = { id: `n${graph.nextId++}`, defId, instanceId, level, x, y };
  graph.nodes.push(node);
  return node;
}

export function addEdge(graph: MindGraph, fromNode: string, fromPort: string, toNode: string, toPort: string): GraphEdge {
  const edge: GraphEdge = { id: `e${graph.nextId++}`, fromNode, fromPort, toNode, toPort };
  graph.edges.push(edge);
  return edge;
}

export function removeNode(graph: MindGraph, nodeId: string): void {
  graph.nodes = graph.nodes.filter((n) => n.id !== nodeId);
  graph.edges = graph.edges.filter((e) => e.fromNode !== nodeId && e.toNode !== nodeId);
}

export function removeEdge(graph: MindGraph, edgeId: string): void {
  graph.edges = graph.edges.filter((e) => e.id !== edgeId);
}

export function nodeForInstance(graph: MindGraph, instanceId: string): GraphNode | undefined {
  return graph.nodes.find((n) => n.instanceId === instanceId);
}

export interface StarterLoadout {
  graph: MindGraph;
  parts: PartInstance[];
  nextPartId: number;
}

/**
 * The starter MechMaxxing: the intrinsic Mech Core feeding a Punch (tiny, legible,
 * runs through the real evaluator). Inventory also seeds a couple of spare parts
 * so the wiring hook is discoverable immediately.
 */
export function createStarterGraph(): StarterLoadout {
  const core = makeStarterPart(MECH_CORE_DEF_ID, MECH_CORE_INSTANCE_ID, 'rare');
  const punch = makeStarterPart('atk.punch', 'p2');
  const spareTuner = makeStarterPart('tuner.crit_rate', 'p3', 'uncommon');
  const spareShoot = makeStarterPart('atk.shoot', 'p4');

  const g = createEmptyGraph();
  const nCore = addNode(g, core.defId, core.instanceId, 60, 150);
  const nPunch = addNode(g, punch.defId, punch.instanceId, 320, 150);
  addEdge(g, nCore.id, 'o1', nPunch.id, 'in');

  return { graph: g, parts: [core, punch, spareTuner, spareShoot], nextPartId: 5 };
}

/** True if a graph already contains the intrinsic Mech Core node. */
export function hasMechCore(graph: { nodes: { instanceId: string | null }[] }): boolean {
  return graph.nodes.some((n) => n.instanceId === MECH_CORE_INSTANCE_ID);
}
